import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import xss from 'xss';
import { sanitiseLocale } from '../../../../i18n/config';
import { ninetyNineLogoDesignUrl, proLogoDesignUrl } from '../../../../utils/urls';
import type { FAQ } from '../../../../components/FAQAccordion/FAQAccordion';

// Allow XSS to allow class attribute on ul elements
const xssOptions = {
    onTagAttr: (tag: string, name: string, value: string) => {
        if (tag === 'ul' && name === 'class') {
            return `${name}="${value}"`;
        }
        return undefined; // Default XSS filtering for everything else
    },
};

export const useFaqData = (): FAQ[] => {
    const { t } = useTranslation();
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);

    const proLogoDesignUrlPath = proLogoDesignUrl(locale);
    const ninetyNineLogoDesignUrlPath = ninetyNineLogoDesignUrl(locale);

    const faqDataRaw = [
        {
            id: 'faq-whats-the-difference',
            question: t('Logomaker GenAi Landing Page > FAQs > whats-the-difference.question'),
            answer: t('Logomaker GenAi Landing Page > FAQs > whats-the-difference.answer'),
        },
        {
            id: 'is-the-logo-design-free',
            question: t('Logomaker GenAi Landing Page > FAQs > is-the-logo-design-free.question'),
            answer: t('Logomaker GenAi Landing Page > FAQs > is-the-logo-design-free.answer'),
        },
        {
            id: 'how-do-i-customize-ai-generated-logo',
            question: t(
                'Logomaker GenAi Landing Page > FAQs > how-do-i-customize-ai-generated-logo.question',
            ),
            answer: t(
                'Logomaker GenAi Landing Page > FAQs > how-do-i-customize-ai-generated-logo.answer',
            ),
        },
        {
            id: 'can-i-add-my-tagline-to-my-logo-design',
            question: t(
                'Logomaker GenAi Landing Page > FAQs > can-i-add-my-tagline-to-my-logo-design.question',
            ),
            answer: t(
                'Logomaker GenAi Landing Page > FAQs > can-i-add-my-tagline-to-my-logo-design.answer',
            ),
        },
        {
            id: 'are-logos-unique-and-copyright-safe',
            question: t(
                'Logomaker GenAi Landing Page > FAQs > are-logos-unique-and-copyright-safe.question',
            ),
            answer: t(
                'Logomaker GenAi Landing Page > FAQs > are-logos-unique-and-copyright-safe.answer',
                {
                    proLogoDesignUrlPath,
                    ninetyNineLogoDesignUrlPath,
                },
            ),
        },
        {
            id: 'can-i-edit-my-logo',
            question: t('Logomaker GenAi Landing Page > FAQs > can-i-edit-my-logo.question'),
            answer: t('Logomaker GenAi Landing Page > FAQs > can-i-edit-my-logo.answer'),
        },
        {
            id: 'what-logo-files-do-i-get',
            question: t('Logomaker GenAi Landing Page > FAQs > what-logo-files-do-i-get.question'),
            answer: t('Logomaker GenAi Landing Page > FAQs > what-logo-files-do-i-get.answer'),
        },
        {
            id: 'can-i-ask-for-professional-designer-support',
            question: t(
                'Logomaker GenAi Landing Page > FAQs > can-i-ask-for-professional-designer-support.question',
            ),
            answer: t(
                'Logomaker GenAi Landing Page > FAQs > can-i-ask-for-professional-designer-support.answer',
            ),
        },
        {
            id: 'can-i-upload-my-own-designs',
            question: t(
                'Logomaker GenAi Landing Page > FAQs > can-i-upload-my-own-designs.question',
            ),
            answer: t('Logomaker GenAi Landing Page > FAQs > can-i-upload-my-own-designs.answer'),
        },
    ];

    return faqDataRaw.map((faq) => {
        return {
            ...faq,
            answer: (
                <div
                    dangerouslySetInnerHTML={{
                        __html: xss(
                            // Parse markdown and add swan-list class to ul elements
                            (marked.parse(faq.answer as string) as string).replace(
                                /<ul>/g,
                                '<ul class="swan-list">',
                            ),
                            xssOptions,
                        ),
                    }}
                />
            ),
        };
    });
};
